/**
 * PhotobookPage — composer un livre photo PDF à partir du catalogue.
 *
 * Flow :
 *  1. Liste de toutes les œuvres (3 statuts confondus) en grille à
 *     gauche, checkbox par carte pour basculer "incluse / exclue".
 *  2. Panneau "Ordre du livre" à droite avec uniquement les œuvres
 *     sélectionnées, drag-drop vertical pour réordonner (dnd-kit).
 *  3. Sticky bar bas : langue FR/EN + bouton « Générer le PDF (N) ».
 *
 * Le backend reçoit `work_ids` dans l'ordre exact — l'ordre des
 * spreads du PDF en découle. Sélection vide = bouton désactivé.
 */

import { useMemo, useState } from 'react'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CheckSquare, Download, FileText, GripVertical, Square } from 'lucide-react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import MediaImage from '@/components/ui/MediaImage'
import Page from '@/components/layout/Page'
import { useGeneratePhotobook } from '@/hooks/usePhotobook'
import { useWorks } from '@/hooks/useWorks'
import type { WorkSummaryResponse } from '@/types/works'

import './PhotobookPage.css'

export default function PhotobookPage() {
  // Récupère TOUTES les œuvres (3 statuts) — l'utilisatrice décide quoi
  // inclure. Cap aligné sur le max BE `GET /api/works` (page_size ≤ 200).
  // Atelier-scale actuel ~31 œuvres, large marge. Si le catalogue grossit
  // au-delà, bumper le cap BE + ajouter une pagination à cette page.
  const worksQuery = useWorks({ page: 1, page_size: 200, sort: 'recent' })

  // Sélection : Set d'IDs inclus.
  const [selected, setSelected] = useState<Set<number>>(new Set())

  // Ordre explicite (work_ids dans l'ordre du livre). Initialisé/étendu
  // au fil des sélections — quand on ajoute une œuvre via checkbox, elle
  // arrive en fin de liste ; quand on la retire, on enlève sa position.
  const [order, setOrder] = useState<number[]>([])

  const [language, setLanguage] = useState<'fr' | 'en'>('fr')

  const generate = useGeneratePhotobook()

  const works = useMemo<WorkSummaryResponse[]>(
    () => worksQuery.data?.items ?? [],
    [worksQuery.data],
  )

  const orderedSelected = useMemo<WorkSummaryResponse[]>(() => {
    const byId = new Map(works.map((w) => [w.id, w]))
    return order.map((id) => byId.get(id)).filter((w): w is WorkSummaryResponse => w !== undefined)
  }, [order, works])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const toggleSelect = (workId: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(workId)) {
        next.delete(workId)
        setOrder((ord) => ord.filter((id) => id !== workId))
      } else {
        next.add(workId)
        setOrder((ord) => [...ord, workId])
      }
      return next
    })
  }

  const selectAll = () => {
    const allIds = works.map((w) => w.id)
    setSelected(new Set(allIds))
    setOrder(allIds)
  }

  const clearSelection = () => {
    setSelected(new Set())
    setOrder([])
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = order.indexOf(Number(active.id))
    const newIndex = order.indexOf(Number(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    setOrder(arrayMove(order, oldIndex, newIndex))
  }

  const handleGenerate = () => {
    if (order.length === 0) return
    generate.mutate(
      { work_ids: order, language },
      {
        onSuccess: () => {
          toast.success(`Livre photo généré (${order.length} œuvres).`)
        },
        onError: () => {
          toast.error('Échec de la génération du PDF.')
        },
      },
    )
  }

  return (
    <Page width="wide">
      <Page.Header
        eyebrow="Édition"
        title="Livre photo"
        subtitle="Sélectionne et réordonne les œuvres pour composer un catalogue PDF."
        actions={
          <div className="photobook-lang-toggle" role="group" aria-label="Langue">
            <Button
              variant={language === 'fr' ? 'primary' : 'quiet'}
              size="sm"
              onClick={() => setLanguage('fr')}
            >
              FR
            </Button>
            <Button
              variant={language === 'en' ? 'primary' : 'quiet'}
              size="sm"
              onClick={() => setLanguage('en')}
            >
              EN
            </Button>
          </div>
        }
      />

      {worksQuery.isLoading ? (
        <p className="photobook-loading">Chargement des œuvres…</p>
      ) : works.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Aucune œuvre"
          description="Importe d'abord des œuvres pour composer un livre."
        />
      ) : (
        <div className="photobook-layout">
          <section className="photobook-pool">
            <header className="photobook-section-head">
              <h2 className="photobook-section-title">Toutes les œuvres</h2>
              <div className="photobook-bulk-actions">
                <Button variant="ghost" size="sm" onClick={selectAll}>
                  <CheckSquare size={14} /> Tout sélectionner
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  disabled={selected.size === 0}
                >
                  <Square size={14} /> Effacer
                </Button>
              </div>
            </header>
            <div className="photobook-grid">
              {works.map((work) => {
                const isSelected = selected.has(work.id)
                return (
                  <div
                    key={work.id}
                    role="button"
                    tabIndex={0}
                    className={`photobook-card${isSelected ? ' photobook-card--selected' : ''}`}
                    onClick={() => toggleSelect(work.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        toggleSelect(work.id)
                      }
                    }}
                    aria-pressed={isSelected}
                  >
                    <div className="photobook-card-thumb">
                      {work.thumb_minio_key ? (
                        <MediaImage minioKey={work.thumb_minio_key} alt={work.title_fr} />
                      ) : (
                        <div className="photobook-card-thumb-empty" />
                      )}
                      <span className="photobook-card-check" aria-hidden="true">
                        {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                      </span>
                    </div>
                    <span className="photobook-card-title">{work.title_fr}</span>
                  </div>
                )
              })}
            </div>
          </section>

          <aside className="photobook-order">
            <header className="photobook-section-head">
              <h2 className="photobook-section-title">
                Ordre du livre
                <span className="photobook-count">{order.length}</span>
              </h2>
            </header>
            {orderedSelected.length === 0 ? (
              <p className="photobook-order-empty">
                Sélectionne des œuvres dans la grille pour les ajouter au livre.
              </p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={order} strategy={verticalListSortingStrategy}>
                  <ol className="photobook-order-list">
                    {orderedSelected.map((work, idx) => (
                      <SortableOrderItem
                        key={work.id}
                        work={work}
                        position={idx + 1}
                        onRemove={() => toggleSelect(work.id)}
                      />
                    ))}
                  </ol>
                </SortableContext>
              </DndContext>
            )}
          </aside>
        </div>
      )}

      <div className="photobook-sticky-bar">
        <span className="photobook-sticky-info">
          {order.length === 0
            ? 'Aucune œuvre sélectionnée'
            : `${order.length} œuvre${order.length > 1 ? 's' : ''} dans le livre · ${language.toUpperCase()}`}
        </span>
        <Button
          variant="primary"
          size="md"
          onClick={handleGenerate}
          disabled={order.length === 0 || generate.isPending}
        >
          <Download size={16} />
          {generate.isPending ? 'Génération…' : 'Générer le PDF'}
        </Button>
      </div>
    </Page>
  )
}

interface SortableOrderItemProps {
  work: WorkSummaryResponse
  position: number
  onRemove: () => void
}

function SortableOrderItem({ work, position, onRemove }: SortableOrderItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: work.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <li ref={setNodeRef} style={style} className="photobook-order-item">
      <span
        className="photobook-order-handle"
        {...attributes}
        {...listeners}
        aria-label="Réordonner"
      >
        <GripVertical size={16} />
      </span>
      <span className="photobook-order-position">{position}</span>
      <span className="photobook-order-thumb">
        {work.thumb_minio_key ? (
          <MediaImage minioKey={work.thumb_minio_key} alt={work.title_fr} />
        ) : (
          <span className="photobook-order-thumb-empty" />
        )}
      </span>
      <span className="photobook-order-title">{work.title_fr}</span>
      <Button variant="ghost" size="sm" onClick={onRemove} aria-label="Retirer">
        Retirer
      </Button>
    </li>
  )
}
